import { absoluteUrl, articleJsonLd, ogImage, seoLinks, clampTitle, clampDescription } from "@/lib/seo";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { BlogArticle } from "@/components/site/BlogViews";
import { CtaBand } from "@/components/site/Page";
import { getPublishedPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/en/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPublishedPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const title = clampTitle(loaderData?.seoTitle || loaderData?.title || "Blog – Swedish International School");
    const description = clampDescription(loaderData?.seoDescription || loaderData?.excerpt || "A post from Swedish International School.");
    const path = `/en/blog/${loaderData?.slug ?? ""}`;
    const cover = loaderData?.coverImageUrl || "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl(path) },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover ? ogImage(`${cover}${cover.includes("?") ? "&" : "?"}w=1280`) : ogImage()),
        { property: "og:locale", content: "en_GB" },
      ],
      links: [
        ...seoLinks(path),
        ...(cover
          ? [
              {
                rel: "preload",
                as: "image",
                href: `${cover}${cover.includes("?") ? "&" : "?"}w=1280`,
                fetchpriority: "high",
              },
            ]
          : []),
      ],
      scripts: loaderData
        ? [
            articleJsonLd({
              path,
              headline: loaderData.title,
              description,
              image: loaderData.coverImageUrl || undefined,
              datePublished: loaderData.publishedAt,
              dateModified: loaderData.updatedAt,
              authorName: loaderData.authorName || undefined,
              locale: "en",
              type: "BlogPosting",
            }),
          ]
        : [],
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      The post could not be loaded right now.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      This post is no longer available.
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData();
  return (
    <>
      <BlogArticle post={post} locale="en" />
      <CtaBand locale="en" />
    </>
  );
}
